import { parseDateInput } from '@/utils/date';

/**
 * Utility functions for determining date status and colors.
 *
 * Dates may come from forms (DD/MM/YYYY), ISO strings (YYYY-MM-DD),
 * or other parsable formats; this module normalizes before comparison.
 */

type DateStatus = 'expired' | 'warning' | 'valid';

function parseDate(value: string | undefined): Date | null {
  return value ? (parseDateInput(value) ?? null) : null;
}

/**
 * Determines the status of a date string.
 * Accepts ISO (YYYY-MM-DD), DD/MM/YYYY, or any Date-parsable string.
 *
 * @param dateString - Expiry date as a string
 * @param warningDays - Days before expiry to show warning (default: 30)
 * @returns DateStatus indicating if expired, warning, or valid
 */
export function getDateStatus(
  dateString: string | undefined,
  warningDays: number = 30
): DateStatus {
  const date = parseDate(dateString);
  if (!date) return 'valid';

  const now = new Date();
  const timeDiff = date.getTime() - now.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

  if (daysDiff < 0) return 'expired';
  if (daysDiff <= warningDays) return 'warning';
  return 'valid';
}

/**
 * Gets the appropriate EntityCardTone for a date status
 */
export function getDateStatusTone(status: DateStatus): 'neutral' | 'warning' | 'danger' {
  switch (status) {
    case 'expired':
      return 'danger';
    case 'warning':
      return 'warning';
    case 'valid':
      return 'neutral';
    default:
      return 'neutral';
  }
}
